import { FileTypes } from 'enums'

import { useBinaryFileContent, useBinaryFileContentFromSignedUrl, useTextFileContent } from 'modules/resultsV2/hooks'
import { UploadedFileProvider } from 'services/results/enums'

import { checkIsBinaryFile, checkIsValidImageFileExtension, getFileExtension } from '../utils'

// File to be fetched from the bucket data
export type BucketFileContent = {
  file: S3FileAsContent
  fileKey: string
  resultId: string
  containerName?: string
  bucket?: string
  provider?: UploadedFileProvider
  storageAccountName?: string
}

// File to be fetch via a signed URL
export type SignedUrlFileContent = {
  fileKey: string
  signedUrl: string
}

export type FileContentProps = BucketFileContent | SignedUrlFileContent

export const useFileContent = (props: FileContentProps) => {
  const isSignedUrlPreview = 'signedUrl' in props
  const fileName = 'file' in props && props.file ? props.file.name : props.fileKey
  const fileExtension = getFileExtension(fileName)?.toLocaleLowerCase() || ''
  const isValidImageFileExtension = !!fileExtension && checkIsValidImageFileExtension(fileExtension as FileTypes)
  const isHTMLFileExtension = fileExtension === FileTypes.HTML
  const isDocxFileExtension = fileExtension === FileTypes.DOCX
  const isPDFFileExtension = fileExtension === FileTypes.PDF
  const isSVGFileExtension = fileExtension === FileTypes.SVG
  const isXLSFileExtension = [FileTypes.XLSX, FileTypes.XLS].includes(fileExtension as FileTypes)
  const isBinaryFile = checkIsBinaryFile(fileExtension)

  const baseFileContentArgs = {
    runId: isSignedUrlPreview ? '' : props.resultId,
    fileKey: props.fileKey,
    ...(!isSignedUrlPreview && 'bucket' in props && props.bucket ? { bucket: props.bucket } : {}),
    ...(!isSignedUrlPreview && 'containerName' in props && props.containerName
      ? { containerName: props.containerName }
      : {}),
    ...(!isSignedUrlPreview && 'provider' in props && props.provider ? { provider: props.provider } : {}),
    ...(!isSignedUrlPreview && 'storageAccountName' in props && props.storageAccountName
      ? { storageAccountName: props.storageAccountName }
      : {})
  }

  const textFileContentQuery = useTextFileContent(
    { ...baseFileContentArgs, fetchSignedUrlOnly: !isHTMLFileExtension && !isSVGFileExtension },
    { enabled: !isSignedUrlPreview && !isBinaryFile }
  )

  // Use a binary file download only for docx, xlsx and xls files
  const binaryFileContentQuery = useBinaryFileContent(
    { ...baseFileContentArgs, fetchSignedUrlOnly: isPDFFileExtension },
    { enabled: !isSignedUrlPreview && isBinaryFile }
  )

  // Docx/XLS binary types can't be rendered straight from a signed URL as text,
  // so fetch the raw bytes from the signed URL and feed them to the binary viewers.
  const isSignedUrlBinaryViewer = isSignedUrlPreview && (isDocxFileExtension || isXLSFileExtension)
  const signedUrlBinaryQuery = useBinaryFileContentFromSignedUrl(isSignedUrlPreview ? props.signedUrl : '', {
    enabled: isSignedUrlBinaryViewer
  })

  return {
    isSignedUrlPreview,
    signedUrl: isSignedUrlPreview ? props.signedUrl : undefined,
    fileExtension,
    isValidImageFileExtension,
    isHTMLFileExtension,
    isDocxFileExtension,
    isPDFFileExtension,
    isSVGFileExtension,
    isXLSFileExtension,
    isSignedUrlBinaryViewer,
    textFileContentQuery,
    binaryFileContentQuery,
    signedUrlBinaryQuery
  }
}
